import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  Autocomplete,
  Switch,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";

import MapPicker from "react-google-map-picker";
import GoogleAddressField from "app/components/google_address";
import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";
import * as XLSX from "xlsx";

export default function OpsActivitiesPage() {
  const title = "Activities";
  usePageTitle(title);

  const { ref } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [activities, setActivities] = useState([]);
  const [dataForms, setDataForms] = useState([]);

  const [activityTitle, setActivityTitle] = useState(null);
  const [selectedDataForm, setSelectedDataForm] = useState(null);
  const [enforcePresence, setEnforcePresence] = useState(false);
  const [openCreateActivityDialog, setOpenCreateActivityDialog] = useState(false);

  async function deleteActivity(activityRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/activities/${activityRef}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateActivityStatus(activityRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/activities/${activityRef}/status.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateActivityEnforcement(activityRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/activities/${activityRef}/enforce.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const activitiesTableColumns = [
    { Header: "Name", accessor: "title", align: "left" },
    { Header: "Data Form", accessor: "data_form", align: "left" },
    { Header: "Enable Geo-Fencing", accessor: "enforce", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildActivitiesRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: row.title,
        data_form: row.data_form,
        enforce: (
          <Switch
            checked={Boolean(row.enforce_presence)}
            onChange={(e) => updateActivityEnforcement(row.reference)}
          />
        ),
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" gap={1}>
              <Tooltip title="Records">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  iconOnly
                  onClick={() => {
                    navigate(`/ops/activities/${row.reference}/records`);
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    list
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Overview">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  iconOnly
                  onClick={() => {
                    navigate(`/ops/activities/${row.reference}/overview`);
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    bar_chart
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title={row.status ? "Disable Activity" : "Enable Activity"}>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  iconOnly
                  onClick={() => {
                    updateActivityStatus(row.reference);
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    {row.status ? "pause" : "play_arrow"}
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Delete Activity?">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    if (window.confirm(`Delete activity?`) === true) {
                      deleteActivity(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    delete
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/ops/activities.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setDataForms(result.data.data.data_forms);

            const activitiesRows = buildActivitiesRows(result.data.data.activities);
            setActivities(activitiesRows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function createActivity() {
    if (activityTitle === null) {
      setSeverity("error");
      setErrMsg("Activity name is required.");
      setOpen(true);
      return;
    }

    if (selectedDataForm === null) {
      setSeverity("error");
      setErrMsg("Data form is required.");
      setOpen(true);
      return;
    }

    setOpenCreateActivityDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      title: activityTitle,
      data_form: selectedDataForm,
      enforce_presence: enforcePresence,
    };

    await axios
      .post("portal/ops/activities/create.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openCreateActivityDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Create Activity</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={0}>
              <MDInput
                name="title"
                margin="dense"
                label="Activity Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setActivityTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Attach Data Form</InputLabel>
                <Select
                  label="Attach Data Form"
                  onChange={(e) => {
                    setSelectedDataForm(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                  placeholder="Select a data form"
                  value={selectedDataForm}
                >
                  <MenuItem value={null}>Select Data Form</MenuItem>
                  {
                    // --> Notice below changes
                    dataForms.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      dataForms.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={3} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enforcePresence}
                    onChange={(e) => setEnforcePresence(e.target.checked)}
                  />
                }
                label="Enable Geo-Fencing"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateActivityDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createActivity();
            }}
          >
            Create Activity
          </MDButton>
        </DialogActions>
      </Dialog>
      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={0} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/ops`);
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox>
              <Button
                color="dark"
                size="small"
                onClick={() => {
                  navigate(`/ops/activities/logs`);
                }}
                startIcon={<Icon>list</Icon>}
              >
                All Activity Logs
              </Button>
            </MDBox>
          </MDBox>

          <MDBox mt={2}>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={3}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>task</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                        Create customizable business operations for your team. From stock taking,
                        installations, merchandizing, customer feedback, enumerations and much more.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={3} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          setActivityTitle(null);
                          setSelectedDataForm(null);
                          setEnforcePresence(false);
                          setOpenCreateActivityDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Activity
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium">
                          Activities
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" gap={0.8}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          size="small"
                          onClick={() => {
                            setActivityTitle(null);
                            setSelectedDataForm(null);
                            setEnforcePresence(false);
                            setOpenCreateActivityDialog(true);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                        {/* <Tooltip title="Activity Logs">
                            <MDButton
                              variant="contained"
                              color="info"
                              iconOnly
                              size="small"
                              onClick={() => {
                                navigate(`/visits/trackers/${ref}/activity/logs`);
                              }}
                            >
                              <Icon sx={{ fontWeight: "bold" }}>list</Icon>
                            </MDButton>
                          </Tooltip> */}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <DataTable
                      table={{ columns: activitiesTableColumns, rows: activities }}
                      isSorted
                      canSearch={false}
                      entriesPerPage={false}
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
